import React, { useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { useWorkspace } from './contexts/WorkspaceContext';
import Layout from './components/Layout';
import toast from 'react-hot-toast';
import { getApiUrl } from './config/axios';
import WorkspaceSelection from './components/WorkspaceSelection';
import Login from './pages/Login';
import Register from './pages/Register';
import OpenChat from './pages/OpenChat';
import Dashboard from './pages/Dashboard';
import Chats from './pages/Chats';
import Templates from './pages/Templates';
import Flows from './pages/Flows';
import CreateFlow from './pages/CreateFlow';
import Contacts from './pages/Contacts';
import EmailTemplates from './pages/EmailTemplates';
import EmailCampaigns from './pages/EmailCampaigns';
import CreateEmailCampaign from './pages/CreateEmailCampaign';
import TemplateCreator from './pages/TemplateCreator';
import Statistics from './pages/Statistics';
import WhatsAppStatistics from './pages/WhatsAppStatistics';
import WhatsAppLogs from './pages/WhatsAppLogs';
import WorkspaceManagement from './pages/WorkspaceManagement';
import WorkspaceUsers from './pages/WorkspaceUsers';
import Settings from './pages/Settings';
import BillingInvoice from './pages/BillingInvoice';
import Pricing from './pages/Pricing';
import Landing from './pages/Landing';
import ApiSharing from './pages/ApiSharing';
import SMTPManagement from './pages/SMTPManagement';
import Chatbot from './pages/Chatbot';
import Training from './pages/Training';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import WhatsAppOnboardingWizard from './pages/WhatsAppOnboardingWizard';

/** Workspace owner/admin only (not members/agents), except subscription renewal flow */
function RequireOwnerOrAdminBilling({ children }) {
  const { currentWorkspace, workspaces, subscriptionInfo } = useWorkspace();
  const role = String(currentWorkspace?.user_role || '').toLowerCase();
  if (['owner', 'admin'].includes(role)) return children;

  // No workspace selected (expired plan / never created): still allow billing to subscribe
  const hasOwnerOrAdminWorkspace = workspaces.some((w) =>
    ['owner', 'admin'].includes(String(w.user_role || '').toLowerCase())
  );
  const needsPurchase =
    subscriptionInfo?.subscription_expired ||
    subscriptionInfo?.requires_purchase ||
    subscriptionInfo?.can_create_workspace === false ||
    workspaces.length === 0;
  if (!currentWorkspace && (needsPurchase || hasOwnerOrAdminWorkspace || subscriptionInfo?.is_super_admin)) {
    return children;
  }

  return <Navigate to="/dashboard" replace />;
}

/** Settings: owner/admin, or any member forced to renew (expired subscription) */
function RequireSettingsAccess({ children }) {
  const { currentWorkspace, subscriptionInfo } = useWorkspace();
  const role = String(currentWorkspace?.user_role || '').toLowerCase();
  if (['owner', 'admin'].includes(role)) return children;
  const mustRenew =
    subscriptionInfo?.subscription_expired && !subscriptionInfo?.is_super_admin;
  if (mustRenew) return children;
  return <Navigate to="/dashboard" replace />;
}

function App() {
  const { user, loading } = useAuth();
  const { currentWorkspace, loading: workspaceLoading, subscriptionInfo } = useWorkspace();
  const location = useLocation();
  const navigate = useNavigate();
  const esRef = useRef(null);

  // Connect SSE for real-time chat notifications
  useEffect(() => {
    if (!user || !currentWorkspace) return;

    // Listen for reconnect events
    const handleReconnect = () => {
      console.log('🔄 SSE reconnect event received');
      // Force reconnection by clearing current connection
      if (esRef.current) {
        try { esRef.current.close(); } catch { }
        esRef.current = null;
      }
    };

    window.addEventListener('sse-reconnect', handleReconnect);

    // Close existing connection if any
    if (esRef.current) {
      try { esRef.current.close(); } catch { }
      esRef.current = null;
    }

    const url = `${getApiUrl()}/api/realtime/events`;
    // Connecting to SSE
    const es = new EventSource(url);
    esRef.current = es;

    es.onopen = () => {
      // SSE connected for real-time updates
    };

    es.addEventListener('ping', (ev) => {
      try {
        JSON.parse(ev.data || '{}');
        // SSE ping received - keeping connection alive
      } catch (err) {
        console.warn('⚠️ Error parsing ping data:', err);
      }
    });

    es.addEventListener('campaign-progress', (ev) => {
      try {
        const data = JSON.parse(ev.data || '{}');
        if (data.workspaceId && currentWorkspace?.id && data.workspaceId !== currentWorkspace.id) {
          return;
        }
        window.dispatchEvent(new CustomEvent('sse:campaign-progress', { detail: data }));
      } catch (err) {
        console.error('❌ Error processing SSE campaign-progress:', err);
      }
    });

    es.addEventListener('new-message', (ev) => {
      try {
        const data = JSON.parse(ev.data || '{}');
        // SSE new-message event received

        // Filter by workspace ID
        if (data.workspaceId && currentWorkspace?.id && data.workspaceId !== currentWorkspace.id) {
          console.log('❌ Message filtered out - wrong workspace:', data.workspaceId, 'vs', currentWorkspace.id);
          return;
        }

        const label = data.fromNumber || data.toNumber || 'Unknown';
        const text = data.messageText || 'New message';
        const contactPhone = data.fromNumber || data.toNumber;

        // Show toast notification
        toast.custom((t) => {
          const openChatFromToast = () => {
            if (!contactPhone) return;
            toast.dismiss(t.id);
            navigate(`/chats?contact=${encodeURIComponent(contactPhone)}`);
          };

          return (
          <div
            role="button"
            tabIndex={0}
            onClick={openChatFromToast}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openChatFromToast();
              }
            }}
            className={`
            max-w-sm w-full bg-white shadow-2xl rounded-xl border border-gray-200 cursor-pointer
            transform transition-all duration-300 ease-in-out
            ${t.visible
              ? 'translate-x-0 opacity-100 scale-100'
              : 'translate-x-full opacity-0 scale-95'
            }
            hover:shadow-xl hover:scale-105
          `}>
            <div className="p-4">
              <div className="flex items-start space-x-3">
                {/* WhatsApp Icon */}
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                    </svg>
                  </div>
                </div>

                {/* Message Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-gray-900">WhatsApp Message</p>
                    <button
                      type="button"
                      data-dismiss-toast
                      onClick={(e) => {
                        e.stopPropagation();
                        toast.dismiss(t.id);
                      }}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <p className="text-sm font-medium text-gray-800 mt-1">{label}</p>
                  <p className="text-sm text-gray-600 mt-1 truncate">{text}</p>
                  <div className="flex items-center mt-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-xs text-gray-500 ml-2">Just now</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          );
        }, {
          duration: 8000,
          position: 'bottom-right',
          className: 'mb-4 mr-4'
        });

        // Dispatch event to Chats component (ONLY ONCE via SSE)
        window.dispatchEvent(new CustomEvent('sse:new-message', { detail: data }));
        // New message dispatched to Chats component
      } catch (err) {
        console.error('❌ Error processing SSE message:', err);
      }
    });

    es.addEventListener('error', (err) => {
      console.error('❌ SSE connection error:', err);
      console.log('📊 SSE ReadyState on error:', es.readyState);
      console.log('📊 SSE URL:', es.url);

      try { es.close(); } catch { }
      if (esRef.current === es) esRef.current = null;

      // Auto-reconnect after 2 seconds (reduced from 3)
      setTimeout(() => {
        if (user && currentWorkspace && !esRef.current) {
          console.log('🔄 Attempting to reconnect SSE...');
          // Re-trigger the useEffect to create a new connection
          window.dispatchEvent(new CustomEvent('sse-reconnect'));
        }
      }, 2000);
    });

    return () => {
      // Closing SSE connection
      window.removeEventListener('sse-reconnect', handleReconnect);
      try { es.close(); } catch { }
      if (esRef.current === es) esRef.current = null;
    };
  }, [user, currentWorkspace, navigate]);

  if (loading || workspaceLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="loading-spinner w-8 h-8 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/open-chat/:token" element={<OpenChat />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  const isExpired =
    subscriptionInfo?.subscription_expired &&
    !subscriptionInfo?.is_super_admin &&
    !subscriptionInfo?.workspace_comped_lifetime;
  const isBillingRoute = location.pathname === '/billing';
  const isSettingsRoute = location.pathname === '/settings';
  const canAccessWhileExpired = isBillingRoute || isSettingsRoute;

  // No workspace selected: still allow Billing so users can pay / renew
  if (!currentWorkspace) {
    if (isBillingRoute) {
      return (
        <div className="min-h-screen bg-gray-50 py-10 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <RequireOwnerOrAdminBilling>
              <BillingInvoice />
            </RequireOwnerOrAdminBilling>
          </div>
        </div>
      );
    }
    // Expired account with no selected workspace → send them to billing plans
    if (isExpired) {
      return <Navigate to="/billing" replace />;
    }
    return <WorkspaceSelection />;
  }

  // Expired subscription: only Billing + Settings (renewal) — do not block /billing itself
  if (isExpired && !canAccessWhileExpired) {
    return <Navigate to="/billing" replace />;
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/analytics" element={<AnalyticsDashboard />} />
        <Route path="/chats" element={<Chats />} />
        <Route path="/templates" element={<Templates />} />
        <Route path="/flows" element={<Flows />} />
        <Route path="/flows/create" element={<CreateFlow />} />
        <Route path="/flows/:id/edit" element={<CreateFlow />} />
        <Route path="/contacts" element={<Contacts />} />
        <Route path="/email-templates" element={<EmailTemplates />} />
        <Route path="/email-campaigns" element={<EmailCampaigns />} />
        <Route path="/create-email-campaign" element={<CreateEmailCampaign />} />
        <Route path="/edit-email-campaign/:id" element={<CreateEmailCampaign />} />
        <Route path="/template-creator" element={<TemplateCreator />} />
        <Route path="/api-sharing" element={<ApiSharing />} />
        <Route path="/chatbot" element={<Chatbot />} />
        <Route path="/chatbot/:chatbotId/training" element={<Training />} />
        <Route path="/statistics" element={<Statistics />} />
        <Route path="/whatsapp-statistics" element={<WhatsAppStatistics />} />
        <Route path="/whatsapp-logs" element={<WhatsAppLogs />} />
        <Route
          path="/setup/whatsapp"
          element={
            <RequireOwnerOrAdminBilling>
              <WhatsAppOnboardingWizard />
            </RequireOwnerOrAdminBilling>
          }
        />
        <Route path="/workspace" element={<WorkspaceManagement />} />
        <Route path="/workspace/users" element={<WorkspaceUsers />} />
        <Route path="/settings" element={<RequireSettingsAccess><Settings /></RequireSettingsAccess>} />
        <Route path="/billing" element={<RequireOwnerOrAdminBilling><BillingInvoice /></RequireOwnerOrAdminBilling>} />
        <Route path="/smtp-management" element={<SMTPManagement />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  );
}

export default App;
